/**
 * Which organizations a signed-in user has a workspace in (office profiles,
 * spec 7.3).
 *
 * The list comes from `public.get_my_org_workspaces()` (migration
 * 20261013210000), which returns every organization where the caller has a
 * direct membership, plus every managed office the caller reaches through a
 * current owner or admin role on its parent company. The parent rule lives in
 * the database; this module only turns rows into workspaces.
 *
 * useActiveOrg and the post-login destination both resolve through
 * {@link loadWorkspaceOrgs}, so "has an org" means the same thing on both paths.
 *
 * Deploy order. The frontend can reach an environment before the migration
 * does (a preview, or the minute between a merge's bundle and its migration).
 * Then the RPC answers PGRST202 (function not found), and every admin would be
 * sent to setup. In that one case the loader falls back to the direct
 * membership read this module replaced. Every other error is returned as is.
 */
import { supabase } from "@/integrations/supabase/client";
import { EFFECTIVE_LOGO_EMBED, withEffectiveLogo, type WithEffectiveEmbed } from "@/lib/orgEffectiveProfile";
import { ELIGIBLE_WORKSPACE_ROLES, orderAdminOrgs } from "@/lib/activeOrgSelection";

/** Where the user's authority in a workspace comes from. */
export type WorkspaceAuthority = "direct" | "parent_owner" | "parent_admin";

export interface WorkspaceOrg {
  id: string;
  name: string;
  slug: string;
  type: string;
  logo_url: string | null;
  created_at: string;
  parent_org_id: string | null;
  /**
   * The role the UI treats the user as holding here (super_admin | admin |
   * editor). For a parent owner or admin of a managed office it is "admin":
   * a managed office never has a direct super_admin, and every owner-only
   * action is decided by the database, not by this value.
   */
  role: string;
  authority: WorkspaceAuthority;
  /** The current direct membership role here, if any (super_admin | admin | editor). */
  direct_role?: string | null;
  /** The role the database reports through the parent company, if any. */
  parent_role?: "owner" | "admin" | null;
}

/** One row of get_my_org_workspaces(). */
export interface WorkspaceRpcRow {
  org_id: string;
  name: string | null;
  slug: string | null;
  type: string | null;
  logo_url: string | null;
  created_at: string | null;
  parent_org_id: string | null;
  direct_role: string | null;
  direct_is_current: boolean | null;
  inherited_role: string | null;
}

const RANK: Record<string, number> = { super_admin: 3, admin: 2, editor: 1 };

/**
 * Rows to workspaces, ordered like useActiveOrg always has (created_at, then id).
 *
 * A row is a workspace when the direct membership is a workspace role AND
 * exactly current (`is_current === true`, the rule activeOrgSelection applies),
 * or when the database reports a parent role ('owner' or 'admin'). When both
 * apply, the higher of the two roles wins, so a direct editor who is also a
 * parent admin gets the admin pages.
 */
export const workspacesFromRows = (rows: WorkspaceRpcRow[]): WorkspaceOrg[] => {
  const out: WorkspaceOrg[] = [];
  for (const r of rows) {
    if (!r?.org_id) continue;
    const direct =
      r.direct_is_current === true &&
      !!r.direct_role &&
      (ELIGIBLE_WORKSPACE_ROLES as readonly string[]).includes(r.direct_role)
        ? r.direct_role
        : null;
    const inherited =
      r.inherited_role === "owner" || r.inherited_role === "admin" ? r.inherited_role : null;
    if (!direct && !inherited) continue;

    const inheritedAsRole = inherited ? "admin" : null;
    const useInherited = !direct || (inheritedAsRole && RANK[inheritedAsRole] > RANK[direct]);
    out.push({
      id: r.org_id,
      name: r.name ?? "",
      slug: r.slug ?? "",
      type: r.type ?? "",
      logo_url: r.logo_url ?? null,
      created_at: r.created_at ?? "",
      parent_org_id: r.parent_org_id ?? null,
      role: useInherited ? (inheritedAsRole as string) : (direct as string),
      authority: useInherited ? (inherited === "owner" ? "parent_owner" : "parent_admin") : "direct",
      direct_role: direct,
      parent_role: inherited,
    });
  }
  return orderAdminOrgs(out);
};

type LoadResult = { orgs: WorkspaceOrg[]; error: null } | { orgs: null; error: unknown };

/** PostgREST's "function not found in the schema cache". */
const isMissingFunction = (error: unknown) =>
  (error as { code?: string } | null)?.code === "PGRST202";

/** The pre-office read: direct memberships only. Used only when the RPC does not exist yet. */
async function loadDirectMembershipRows(userId: string): Promise<{ rows: WorkspaceRpcRow[] | null; error: unknown }> {
  const { data, error } = await supabase
    .from("organization_members")
    .select(`role, is_current, org_id, organizations:org_id ( id, name, slug, type, created_at, parent_org_id, ${EFFECTIVE_LOGO_EMBED} )`)
    .eq("user_id", userId)
    .in("role", [...ELIGIBLE_WORKSPACE_ROLES]);
  if (error) return { rows: null, error };
  type Row = {
    role: string | null;
    is_current: boolean | null;
    org_id: string;
    organizations: ({
      id: string; name: string; slug: string; type: string;
      created_at: string; parent_org_id: string | null;
    } & WithEffectiveEmbed) | null;
  };
  const rows = ((data ?? []) as unknown as Row[])
    .filter((r) => !!r.organizations)
    .map((r) => ({
      org_id: r.organizations!.id,
      name: r.organizations!.name,
      slug: r.organizations!.slug,
      type: r.organizations!.type,
      // The logo as shown (office profiles 2B-4), as the RPC returns it.
      logo_url: withEffectiveLogo(r.organizations!).logo_url,
      created_at: r.organizations!.created_at,
      parent_org_id: r.organizations!.parent_org_id ?? null,
      direct_role: r.role,
      direct_is_current: r.is_current,
      inherited_role: null,
    }));
  return { rows, error: null };
}

/** The signed-in user's workspaces. `userId` is used only by the fallback read. */
export async function loadWorkspaceOrgs(userId: string): Promise<LoadResult> {
  const { data, error } = await supabase.rpc("get_my_org_workspaces" as never);
  if (!error) {
    return { orgs: workspacesFromRows(Array.isArray(data) ? (data as WorkspaceRpcRow[]) : []), error: null };
  }
  if (!isMissingFunction(error)) return { orgs: null, error };

  const fallback = await loadDirectMembershipRows(userId);
  if (fallback.error) return { orgs: null, error: fallback.error };
  return { orgs: workspacesFromRows(fallback.rows ?? []), error: null };
}

export type MemberRole = "super_admin" | "admin" | "editor";

export interface MemberControls {
  /** Change roles and remove members (owner-only actions, 6.3). */
  canManageMembers: boolean;
  /**
   * Invite, and cancel invitations to roles in {@link assignableRoles}: owners
   * and admins, direct or through the company (Jesse's decision 1, #932). An
   * owner invitation is sent or cancelled only by an owner.
   */
  canInvite: boolean;
  /** Roles the user may give in an invitation or a role change. */
  assignableRoles: MemberRole[];
}

/**
 * What the members section offers, from where the user's authority comes from
 * (Jesse's review of #929). Role changes and removal are owner-only: a current
 * direct super_admin, or a current owner of the parent company of a managed
 * office (which never has a direct owner). A parent admin never gets them, and
 * a person who is both a direct office admin and a parent owner does. A managed
 * office never takes a super_admin. The database decides every action again;
 * this only decides what is shown.
 */
export function memberControls(ws: { direct_role?: string | null; parent_role?: string | null } | null | undefined): MemberControls {
  const directOwner = ws?.direct_role === "super_admin";
  const parentOwner = ws?.parent_role === "owner";
  const managedOffice = ws?.parent_role === "owner" || ws?.parent_role === "admin";
  const canManageMembers = directOwner || parentOwner;
  if (canManageMembers) {
    return { canManageMembers, canInvite: true, assignableRoles: managedOffice ? ["admin", "editor"] : ["super_admin", "admin", "editor"] };
  }
  const anyAdmin = ws?.direct_role === "admin" || ws?.parent_role === "admin";
  return { canManageMembers: false, canInvite: anyAdmin, assignableRoles: anyAdmin ? ["admin", "editor"] : [] };
}
